import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/navigation/NavigationBar';
import SearchBar from '../../components/ui/SearchBar';
import ExportModal from '../../components/ui/ExportModal';
import { useNotification } from '../../components/ui/NotificationToast';

import Button from '../../components/ui/Button';
import PromptTable from './components/PromptTable';
import FilterPanel from './components/FilterPanel';
import UsageAnalytics from './components/UsageAnalytics';
import FolderOrganizer from './components/FolderOrganizer';
import BulkActions from './components/BulkActions';

const PromptHistory = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useNotification();
  const [activeTab, setActiveTab] = useState('prompts');
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);

  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    aiTool: 'all',
    useCase: 'all',
    quality: 'all',
    favoritesOnly: false
  });

  const [folders, setFolders] = useState([
    { id: 'f1', name: 'Marketing Campaigns', count: 24 },
    { id: 'f2', name: 'Code Snippets', count: 18 },
    { id: 'f3', name: 'Content Ideas', count: 32 },
    { id: 'f4', name: 'Design Prompts', count: 15 }
  ]);

  const mockPrompts = [
    {
      id: 1,
      date: '01/07/2026',
      tool: 'ChatGPT',
      toolIcon: 'MessageSquare',
      useCase: 'Content',
      snippet: 'Create a comprehensive blog post about sustainable living practices that can be implemented in urban environments...',
      fullText: `Create a comprehensive blog post about sustainable living practices that can be implemented in urban environments. The post should include:\n\n1. Introduction to urban sustainability challenges\n2. 10 practical tips for reducing carbon footprint in cities\n3. Case studies of successful urban sustainability initiatives\n4. Resources for readers to learn more\n5. Call-to-action for community involvement\n\nTone: Informative yet conversational\nLength: 1500-2000 words\nTarget audience: Urban millennials interested in environmental issues`,
      qualityScore: 92,
      isFavorite: true
    },
    {
      id: 2,
      date: '01/06/2026',
      tool: 'Claude',
      toolIcon: 'Bot',
      useCase: 'Coding',
      snippet: 'Write a Python function that implements a binary search algorithm with error handling and comprehensive documentation...',
      fullText: `Write a Python function that implements a binary search algorithm with error handling and comprehensive documentation. Requirements:\n\n- Function should accept a sorted list and target value\n- Include type hints for all parameters and return values\n- Add docstring with examples\n- Implement proper error handling for edge cases\n- Include unit tests using pytest\n- Follow PEP 8 style guidelines\n- Add comments explaining the algorithm logic`,
      qualityScore: 88,
      isFavorite: true
    },
    {
      id: 3,
      date: '01/05/2026',
      tool: 'Midjourney',
      toolIcon: 'Image',
      useCase: 'Design',
      snippet: 'Generate a futuristic cityscape at sunset with flying vehicles, holographic advertisements, and diverse architecture...',
      fullText: `Generate a futuristic cityscape at sunset with flying vehicles, holographic advertisements, and diverse architecture blending traditional and modern styles. Style: Cyberpunk meets solarpunk aesthetic. Lighting: Golden hour with neon accents. Composition: Wide-angle view from elevated perspective. Details: Include green spaces, solar panels, and sustainable technology elements. Color palette: Warm oranges and purples with electric blue highlights. --ar 16:9 --v 6 --style raw`,
      qualityScore: 95,
      isFavorite: false
    },
    {
      id: 4,
      date: '01/04/2026',
      tool: 'DALL-E',
      toolIcon: 'Palette',
      useCase: 'Marketing',
      snippet: 'Create a professional product photography style image of an eco-friendly water bottle on a minimalist background...',
      fullText: `Create a professional product photography style image of an eco-friendly water bottle on a minimalist background. The bottle should be made of sustainable materials with a modern design. Include natural lighting that highlights the product's texture and eco-friendly features. Background should be clean white or soft pastel. Add subtle shadows for depth. Style: Commercial product photography, high-end catalog quality. Resolution: High detail, suitable for print and web use.`,
      qualityScore: 85,
      isFavorite: true
    },
    {
      id: 5,
      date: '01/03/2026',tool: 'Gemini',toolIcon: 'Sparkles',useCase: 'Business',snippet: 'Analyze the current trends in remote work technology and provide strategic recommendations for a mid-sized company...',
      fullText: `Analyze the current trends in remote work technology and provide strategic recommendations for a mid-sized company transitioning to a hybrid work model. Include:\n\n1. Overview of leading remote collaboration tools\n2. Cost-benefit analysis of different solutions\n3. Security considerations and best practices\n4. Employee productivity metrics and monitoring\n5. Change management strategies\n6. Implementation timeline and milestones\n7. ROI projections for first year\n\nFormat: Executive summary followed by detailed sections with data visualization suggestions`,
      qualityScore: 78,
      isFavorite: false
    },
    {
      id: 6,
      date: '01/02/2026',tool: 'ChatGPT',toolIcon: 'MessageSquare',useCase: 'Education',snippet: 'Design a lesson plan for teaching basic programming concepts to middle school students using interactive activities...',
      fullText: `Design a lesson plan for teaching basic programming concepts to middle school students using interactive activities and games. The lesson should cover:\n\n- Variables and data types\n- Conditional statements\n- Loops and iteration\n- Functions and modularity\n\nInclude: Learning objectives, materials needed, step-by-step activities, assessment methods, and extension activities for advanced students. Duration: 90 minutes. Teaching style: Hands-on, collaborative learning with real-world examples.`,
      qualityScore: 90,
      isFavorite: true
    },
    {
      id: 7,
      date: '01/01/2026',tool: 'Claude',toolIcon: 'Bot',useCase: 'Content',snippet: 'Write a series of social media posts for a new fitness app launch, including captions for Instagram, Twitter, and LinkedIn...',
      fullText: `Write a series of social media posts for a new fitness app launch, including captions for Instagram, Twitter, and LinkedIn. Each platform should have 5 posts covering:\n\n1. App announcement and key features\n2. User testimonial highlight\n3. Behind-the-scenes development story\n4. Limited-time launch offer\n5. Call-to-action for downloads\n\nInstagram: Visual-focused, emoji-rich, hashtag strategy\nTwitter: Concise, engaging, thread-worthy\nLinkedIn: Professional tone, business value focus\n\nInclude suggested posting schedule and engagement tactics.`,
      qualityScore: 87,
      isFavorite: false
    },
    {
      id: 8,
      date: '12/31/2025',tool: 'Copilot',toolIcon: 'Code',useCase: 'Coding',snippet: 'Generate a React component for a responsive navigation menu with dropdown functionality and mobile hamburger menu...',
      fullText: `Generate a React component for a responsive navigation menu with dropdown functionality and mobile hamburger menu. Requirements:\n\n- Use React hooks (useState, useEffect, useRef)\n- Implement smooth animations for transitions\n- Support keyboard navigation for accessibility\n- Include ARIA labels and roles\n- Mobile-first responsive design\n- Support for nested dropdown menus\n- Active link highlighting\n- Close menu on outside click\n- TypeScript types included\n- Styled with Tailwind CSS\n- Include usage examples and prop documentation`,
      qualityScore: 93,
      isFavorite: true
    }
  ];

  const [prompts, setPrompts] = useState(mockPrompts);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      aiTool: 'all',
      useCase: 'all',
      quality: 'all',
      favoritesOnly: false
    });
    showInfo('Filters Cleared', 'All filters have been reset');
  };

  const handleSelectPrompt = (id) => {
    setSelectedPrompts(prev =>
      prev?.includes(id) ? prev?.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedPrompts(checked ? prompts?.map(p => p?.id) : []);
  };

  const handleEdit = (prompt) => {
    showInfo('Edit Prompt', `Editing: ${prompt?.snippet?.substring(0, 50)}...`);
    navigate('/prompt-generator', { state: { editPrompt: prompt } });
  };

  const handleDuplicate = (prompt) => {
    const newPrompt = {
      ...prompt,
      id: Date.now(),
      date: new Date()?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      snippet: `${prompt?.snippet} (Copy)`
    };
    setPrompts(prev => [newPrompt, ...prev]);
    showSuccess('Prompt Duplicated', 'A copy has been created successfully');
  };

  const handleCopy = (prompt) => {
    navigator.clipboard?.writeText(prompt?.fullText);
    showSuccess('Copied to Clipboard', 'Prompt text has been copied');
  };

  const handleShare = (prompt) => {
    showInfo('Share Prompt', 'Share functionality would open here');
  };

  const handleDelete = (prompt) => {
    setPrompts(prev => prev?.filter(p => p?.id !== prompt?.id));
    showSuccess('Prompt Deleted', 'The prompt has been removed from your history');
  };

  const handleBulkExport = () => {
    const selectedData = prompts?.filter(p => selectedPrompts?.includes(p?.id));
    setIsExportModalOpen(true);
  };

  const handleBulkDelete = () => {
    setPrompts(prev => prev?.filter(p => !selectedPrompts?.includes(p?.id)));
    showSuccess('Prompts Deleted', `${selectedPrompts?.length} prompt(s) have been deleted`);
    setSelectedPrompts([]);
  };

  const handleMoveToFolder = () => {
    showInfo('Move to Folder', 'Folder selection dialog would open here');
  };

  const handleCreateFolder = (name) => {
    const newFolder = {
      id: `f${Date.now()}`,
      name,
      count: 0
    };
    setFolders(prev => [...prev, newFolder]);
    showSuccess('Folder Created', `"${name}" has been created`);
  };

  const handleRenameFolder = (id, newName) => {
    setFolders(prev => prev?.map(f => f?.id === id ? { ...f, name: newName } : f));
    showSuccess('Folder Renamed', 'Folder name has been updated');
  };

  const handleDeleteFolder = (id) => {
    setFolders(prev => prev?.filter(f => f?.id !== id));
    showSuccess('Folder Deleted', 'The folder has been removed');
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  useEffect(() => {
    document.title = 'Prompt History | Get Prompt';
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 pb-24 lg:pb-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Prompt History
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage and organize your saved AI prompts
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={() => setIsExportModalOpen(true)}
              >
                Export
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/prompt-generator')}
              >
                New Prompt
              </Button>
            </div>
          </div>

          <SearchBar
            onSearch={handleSearch}
            placeholder="Search prompts by title, tool, or use case..."
            showResults={false}
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-border overflow-x-auto">
          <button
            onClick={() => setActiveTab('prompts')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === 'prompts' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            All Prompts
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === 'analytics' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Analytics
          </button>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4 md:space-y-6">
            <FolderOrganizer
              folders={folders}
              selectedFolder={selectedFolder}
              onFolderSelect={setSelectedFolder}
              onCreateFolder={handleCreateFolder}
              onRenameFolder={handleRenameFolder}
              onDeleteFolder={handleDeleteFolder}
            />
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              isOpen={isFilterOpen}
              onToggle={() => setIsFilterOpen(!isFilterOpen)}
            />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'prompts' ? (
              <PromptTable
                prompts={prompts}
                selectedPrompts={selectedPrompts}
                onSelectPrompt={handleSelectPrompt}
                onSelectAll={handleSelectAll}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onCopy={handleCopy}
                onShare={handleShare}
                onDelete={handleDelete}
              />
            ) : (
              <UsageAnalytics />
            )}
          </div>
        </div>
      </main>
      {/* Bulk Actions */}
      <BulkActions
        selectedCount={selectedPrompts?.length}
        onExport={handleBulkExport}
        onDelete={handleBulkDelete}
        onMoveToFolder={handleMoveToFolder}
        onClearSelection={() => setSelectedPrompts([])}
      />
      {/* Export Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        data={prompts?.filter(p => selectedPrompts?.includes(p?.id))}
        title="Export Prompts"
      />
    </div>
  );
};

export default PromptHistory;